// Readiness card — the structured wire contract for the "refine an issue to
// fully READY" gate. Types-only (Zod schemas + inferred types).
//
// `@loom/shared` cannot import `@loom/workflows` or `@loom/mcp-server`, so the
// one shape the server gate tool, the sandbox code-evidence hook, and the web
// renderer must all agree on byte-for-byte lives here.
//
// Anti-fabrication is STRUCTURAL, not prompt-based: a slot is either
// `proposed` with a machine-resolvable `provenance` locator, or `needs_input`
// with the question to ask. The model writes ONLY pointers; deterministic code
// re-resolves every locator (user quotes against the session event-log on the
// server; code quotes against the cloned repo in the sandbox PreToolUse hook)
// and denies on a miss. There is no schema branch for an ungrounded value, so
// "I made this up" is unrepresentable.

import { z } from "zod";

import { cardBreakdownSchema } from "./breakdown.js";
import { comprehensionObjectionSchema } from "./comprehension.js";
import { cardSizeEstimateSchema } from "./sizing.js";
import { orderedFields, type ReadinessSpec } from "./spec.js";

// ---------------------------------------------------------------------------
// Machine-resolvable provenance — the model writes ONLY pointers.
// ---------------------------------------------------------------------------

/**
 * Evidence rooted in a specific user turn. `messageSeq` is the
 * `AgentEventEnvelope.seq` of a `user_message` event; the server re-reads that
 * event from the session event-log and asserts `quote` appears verbatim in it.
 */
export const userProvenanceSchema = z.object({
  source: z.literal("user"),
  messageSeq: z.number().int().nonnegative(),
  quote: z.string().min(1).max(2000),
});
export type UserProvenance = z.infer<typeof userProvenanceSchema>;

/**
 * Evidence rooted in the cloned repo. The sandbox PreToolUse hook reads `path`
 * (relative to the workspace root) at `[startLine, endLine]` and asserts
 * `quote` is present BEFORE the tool call reaches the server — the server
 * itself cannot read the sandbox working tree.
 */
export const codeProvenanceSchema = z.object({
  source: z.literal("code"),
  path: z.string().min(1).max(400),
  startLine: z.number().int().positive(),
  endLine: z.number().int().positive(),
  quote: z.string().min(1).max(2000),
});
export type CodeProvenance = z.infer<typeof codeProvenanceSchema>;

/**
 * The ONLY fields a `{source:"code"}` slot may green.
 *
 * A code locator proves a string exists at some lines in the repo. It proves
 * NOTHING about the slot's `value`: the sandbox hook (readiness-code-gate.ts)
 * reads `provenance` and never `value`, and the server cannot compare them
 * because it cannot read the sandbox tree. So `source:"code"` is not a
 * verification lane at all — it is the lane for the two fields the PM directive
 * puts the AGENT in charge of deciding, with the citation as a tether to "I
 * actually read this repo".
 *
 * Every other field on the card — role / means / ends / acceptance_criteria /
 * milestone / priority — is a statement about what the USER wants, and no repo
 * quote can establish that. Before this list a card could reach allGreen with
 * zero user-grounded slots.
 *
 * Deliberately a CONSTANT, not a `ReadinessSpec` field: a project's `.loom.yml`
 * must not be able to widen the set of fields the agent may write unasked. And
 * deliberately hard-coded names rather than `dodFieldName(spec)`: a custom field
 * name cannot be PROPOSED either (`proposedSlotSchema.field` is
 * `z.enum(SLOT_FIELDS)`), so a spec-driven set would widen nothing and only
 * invite the next reader to make it configurable.
 */
export const CODE_SOURCEABLE_FIELDS: ReadonlySet<string> = new Set<string>([
  "dod_criteria",
  "story_points",
]);

export const isCodeSourceableField = (field: string): boolean =>
  CODE_SOURCEABLE_FIELDS.has(field);

export const provenanceSchema = z.discriminatedUnion("source", [
  userProvenanceSchema,
  codeProvenanceSchema,
]);
export type ProvenanceRef = z.infer<typeof provenanceSchema>;

// ---------------------------------------------------------------------------
// The scrumban card's slots.
//
// `assignee` and `sprint` are intentionally absent in v1: neither is writable
// through the GitLab REST proxy (no username->id resolver; CE iterations are
// GraphQL-only). They ship behind a follow-up flag once that plumbing exists.
// ---------------------------------------------------------------------------

export const SLOT_FIELDS = [
  "role",
  "means",
  "ends",
  "acceptance_criteria",
  "dod_criteria",
  "story_points",
  "milestone",
  "priority",
] as const;
export type SlotField = (typeof SLOT_FIELDS)[number];

// ---------------------------------------------------------------------------
// Widget spec — a DISPLAY-ONLY input hint the agent may attach to a
// `needs_input` slot so the chat renders a purpose-built answer control
// (priority picker, milestone dropdown, list builder, …) instead of a bare
// "type your answer" box. It is NOT a value channel: when the user picks/types,
// the chat sends a normal `user_message` whose text the agent then quotes
// verbatim through the usual `{source:"user", quote}` locator — so the gate's
// quote-scan grounding is untouched and a widget can never fill a slot itself.
//
// `option.value` is the EXACT text the widget sends (and the agent must quote);
// `option.label` is display only. They differ on purpose: the user sees "High"
// while the canonical, collision-proof send-text is "Priority: High". The gate
// is pure normalized-substring containment with no word boundaries, so a bare
// "5"/"High" would false-green off an unrelated earlier turn — every short
// answer must be self-labelled, which is what `value` carries.
// ---------------------------------------------------------------------------

export const widgetOptionSchema = z
  .object({
    value: z.string().min(1).max(400),
    label: z.string().min(1).max(200),
  })
  .strict();
export type WidgetOption = z.infer<typeof widgetOptionSchema>;

export const widgetSpecSchema = z.discriminatedUnion("type", [
  // Free text (role / means / ends). Sends the user's words verbatim; every
  // renderer presents this as a growing multiline box, not a single line.
  z.object({ type: z.literal("text") }).strict(),
  // Add/remove rows (acceptance_criteria / dod_criteria). Submits ALL rows in
  // ONE newline-joined message so the agent can quote a contiguous span — the
  // gate never stitches a quote across turns.
  z.object({ type: z.literal("list_builder") }).strict(),
  // Agent-provided fixed choices (priority). `value` is the labelled send-text.
  z
    .object({
      type: z.literal("single_select"),
      options: z.array(widgetOptionSchema).min(1).max(24),
    })
    .strict(),
  // Client fetches options live (milestone) — the agent supplies only the
  // source, never enumerates the list.
  z
    .object({ type: z.literal("dynamic_select"), source: z.literal("milestone") })
    .strict(),
]);
export type WidgetSpec = z.infer<typeof widgetSpecSchema>;

// ---------------------------------------------------------------------------
// Draft answer — Loom's OWN proposed answer to a `needs_input` slot, carried as
// TYPED ROWS rather than prose inside the question.
//
// Why it exists: before this channel the agent had nowhere to put the criteria
// it had already worked out, so it wrote them into the `question` as examples
// ("e.g. the export contains the VAT total, …") and the user retyped them by
// hand. The rows belong in the answer control, not in the question — a question
// carrying its own examples is a question that has already been answered
// badly.
//
// It is NOT a value channel, on exactly the same terms as `widget`: a slot
// carrying a draft is still `needs_input`, still ungrounded, and still cannot
// green. The draft becomes a value only when the user SENDS it — the widget
// serialises the rows through the same `buildListAnswer` every typed answer
// uses, the message is a normal user turn, and the agent must quote it verbatim
// like any other. The gate is untouched.
//
// One row per criterion. A list field's rows go into the list builder; a
// single-row draft on a text field is the whole text. See `WidgetProps.suggested`
// (apps/web) for which of the two the surface pre-fills and which it merely
// offers — a list is inspectable and deletable row by row, a paragraph in a
// text box reads as the user's own sentence.
// ---------------------------------------------------------------------------

/** Hard cap on draft rows. More criteria than this is a spec, not a card. */
export const MAX_DRAFT_ROWS = 12;

export const draftSchema = z
  .array(z.string().min(1).max(400))
  .min(1)
  .max(MAX_DRAFT_ROWS);
export type DraftRows = z.infer<typeof draftSchema>;

export const proposedSlotSchema = z.object({
  status: z.literal("proposed"),
  field: z.enum(SLOT_FIELDS),
  value: z.string().min(1),
  provenance: provenanceSchema,
});
export type ProposedSlot = z.infer<typeof proposedSlotSchema>;

export const needsInputSlotSchema = z.object({
  status: z.literal("needs_input"),
  field: z.enum(SLOT_FIELDS),
  question: z.string().min(1).max(500),
  /** Optional input affordance for the chat. Display-only; the answer still
   *  round-trips as a quotable user message (see widgetSpecSchema). */
  widget: widgetSpecSchema.optional(),
  /** Loom's own draft answer, one entry per row (see draftSchema). Display-only
   *  and NOT a value channel: the slot stays `needs_input` until the user sends
   *  it back as their own message and the agent quotes that message. */
  draft: draftSchema
    .optional()
    .describe(
      "YOUR draft answer for this field, one entry per row — the answer " +
        "control opens with these rows already in it, and the user edits, " +
        "deletes or adds to them instead of typing from scratch. REQUIRED " +
        "for acceptance_criteria: propose the criteria you would write, and " +
        "keep the `question` a plain question with NO examples in it (the " +
        "examples belong here, where the user can actually edit them). This " +
        "does NOT ground the slot: it stays needs_input until the user sends " +
        "an answer back and you quote that answer verbatim, exactly as if " +
        "they had typed every word.",
    ),
});
export type NeedsInputSlot = z.infer<typeof needsInputSlotSchema>;

export const cardSlotSchema = z.discriminatedUnion("status", [
  proposedSlotSchema,
  needsInputSlotSchema,
]);
export type CardSlot = z.infer<typeof cardSlotSchema>;

// ---------------------------------------------------------------------------
// propose_card INPUT (model-facing). `projectId` / `userId` are session-bound
// and merged in server-side, never seen by the model. Plain `z.object` — NOT
// `.refine()` (which would break the omit/strip session-field pattern).
// ---------------------------------------------------------------------------

export const proposeCardInputShape = {
  issueIid: z.number().int().positive(),
  slots: z.array(cardSlotSchema).min(1),
} as const;
export const proposeCardInputSchema = z.object(proposeCardInputShape);
export type ProposeCardInput = z.infer<typeof proposeCardInputSchema>;

// ---------------------------------------------------------------------------
// Per-slot verdict the server/hook computes — the half the model cannot author.
//   green       — locator resolved; evidence verified verbatim
//   red         — evidence genuinely absent (bad citation / quote not found)
//   needs_input — the slot is an open question to the user
//   unavailable — infra/eviction soft verdict (retryable, NOT fabrication)
// ---------------------------------------------------------------------------

export const SLOT_VERDICTS = [
  "green",
  "red",
  "needs_input",
  "unavailable",
] as const;
export type SlotVerdictKind = (typeof SLOT_VERDICTS)[number];

/**
 * Where a green's value came from. `user`: the user's own words in this
 * session (the grounding invariant: the value IS their bytes). `code`: the
 * repo, for `CODE_SOURCEABLE_FIELDS` only. `meeting`: a verified quote from
 * the meeting that filed this issue. Only `user` is the user's own
 * bytes; a client must never treat `code` or `meeting` that way.
 */
export const RESOLVED_SOURCES = ["user", "code", "meeting"] as const;
export type ResolvedSource = (typeof RESOLVED_SOURCES)[number];

/**
 * A `meeting` green that came from an ANSWER given on the meeting page (the
 * owner) or through its magic link (a link holder), not from a quote
 * (refine light, owner decision 2026-10-13: such an answer counts
 * directly). Display-only provenance: the surfaces say "Auf der Meeting-Seite
 * beantwortet" / "über den Link beantwortet" and do not ask the user to
 * confirm it again; the value stays editable. Still never the user's own
 * bytes (`resolvedSource` stays `meeting`).
 */
export const meetingAnswerSchema = z.object({
  via: z.enum(["owner", "link"]),
  answeredAt: z.string(),
});
export type MeetingAnswerProvenance = z.infer<typeof meetingAnswerSchema>;

export const slotVerdictSchema = z.object({
  field: z.enum(SLOT_FIELDS),
  verdict: z.enum(SLOT_VERDICTS),
  reason: z.string(),
  resolvedSource: z.enum(RESOLVED_SOURCES).nullable(),
  /** The value the model DERIVED for a `proposed` slot, echoed verbatim so a
   *  representation (the Focus surface) can show "Loom set <field> = <value>"
   *  for the user to REVIEW. Present only on proposed (green/red/unavailable)
   *  verdicts; absent on `needs_input` (which carries its `question` in
   *  `reason`). Display-only — like `widget`, it is NOT load-bearing for the
   *  gate; the verdict + acceptToken remain the sole apply authority. */
  value: z.string().optional(),
  /** Echoed from the `needs_input` slot so the chat can render the answer
   *  affordance next to the verdict. Present only on `needs_input` verdicts
   *  carrying a widget; never load-bearing for the gate (verdicts are the
   *  authority). Adding it here auto-propagates to the web TimelineEntry,
   *  ReadinessCard props, and the readiness_card_pending event. */
  widget: widgetSpecSchema.optional(),
  /** Echoed from the `needs_input` slot's `draft` so the answer control can
   *  open with Loom's rows in it. Same display-only terms as `widget`: it never
   *  moves a verdict, and the rows only become a value once the user sends
   *  them. Absent on every other verdict kind. */
  draft: draftSchema.optional(),
  /**
   * The user has explicitly SKIPPED this optional field.
   *
   * A skip used to be mount-local browser state and nothing else: it sent
   * nothing, so the agent never learned, the persisted card kept the slot at
   * `needs_input` forever, and the mask died on any remount — the question came
   * straight back. This is the same path `widget` and `draft` ride (slot →
   * FieldState → SlotVerdict → the wire → Focus), so a reload inherits the
   * decision. Only ever set for a field the spec marks OPTIONAL: a skip is not
   * offered on a gating field and a forged frame cannot retire one.
   */
  declined: z.boolean().optional(),
  /** A green from a meeting-page answer (`meetingAnswerSchema`); greens only. */
  meetingAnswer: meetingAnswerSchema.optional(),
  /**
   * This non-green verdict is the SERVER REFUSING THE MODEL, not the agent
   * asking the user.
   *
   * The two were indistinguishable on the wire, and that is what published a
   * gate's model-facing repair instruction as a question headline. Live: the
   * user answered `role` in German, the model quoted their whole sentence, the
   * shape bound (`isRoleShaped`) refused it, and `mintBound.reason()` — "Who is
   * this for? Name them in a few words — a person, a team or a system, not a
   * sentence." — reached the screen verbatim, in English, under the same kicker
   * as the first ask. 27 seconds later the model trimmed its own quote by itself,
   * the field greened and the question vanished mid-typing. The ANSWER was never
   * lost; the refusal was published.
   *
   * So a consumer must never render `reason` for one of these, and a surface
   * that tracks "the user already answered this field" must suppress it exactly
   * as it suppresses a `red` — a refused proposal is the model's to repair, and
   * it usually does, in one turn, at no cost to the user.
   *
   * Set on any non-green verdict resolved from a slot the agent sent
   * `status: "proposed"` (shape refusal, grounding miss, `unavailable`) and on a
   * server-authored downgrade. NEVER set on a slot the agent sent
   * `status: "needs_input"` — that IS the agent asking, and it must still reach
   * the user even on a field they already answered. OPTIONAL: an old server
   * omits it and every consumer keeps today's behaviour.
   */
  gateRefusal: z.boolean().optional(),
  /**
   * This green's value was REWRITTEN by the model over a value the user had
   * already grounded, with no new user turn behind it.
   *
   * The one green worth REMARKING ON. Merge rule 2 lets a later `propose_card`
   * narrow or re-carve a verified value — the live shape is the model re-quoting
   * a shorter span of the same turn — and nothing told the user. Stamped only
   * when the replacement grounds at a seq no LATER than the value it replaces
   * (see `mergeFields`): a value grounded in a later turn is the user speaking
   * again, i.e. the revision working as intended, and putting that back in front
   * of them is the re-ask this flag exists to prevent.
   *
   * A NOTE, never a step, and consumers must keep it that way. The flag is
   * server state that only a later `propose_card` clears, so no send-free client
   * act can dismiss anything gated on it — a queue step built on it survives its
   * own dismissal and re-pins forever, taking the held-answer flush down with it.
   * Focus renders it as a line on the ready-preview row, beside the rewritten
   * value and its Edit control: idempotent, unmissable, and incapable of
   * blocking Accept.
   */
  revised: z.boolean().optional(),
});
export type SlotVerdict = z.infer<typeof slotVerdictSchema>;

/** Mirrors `@loom/workflows` `Verdict` (shared cannot import workflows). */
export const investVerdictSchema = z.object({
  criterion: z.string(),
  level: z.enum(["ok", "warn", "fail"]),
  evidence: z.string(),
  suggestion: z.string().nullable(),
});
export type InvestVerdict = z.infer<typeof investVerdictSchema>;

// ---------------------------------------------------------------------------
// propose_card OUTPUT — the all-green authority. `acceptToken` is present ONLY
// when `allGreen`; it is an HMAC over (sessionId, issueIid, slotHash, exp)
// keyed with LOOM_BRIDGE_SECRET, re-verified at apply time so a stale or
// cross-session green cannot drive a write.
// ---------------------------------------------------------------------------

/** One `ProposeCardOutput.technicalText` row. `rows` counts the dropped draft
 *  rows (LINES, as `draftRowsOf` counts them) on a `draft` row. */
export const technicalTextReportSchema = z.object({
  field: z.string(),
  part: z.enum(["question", "draft"]),
  rows: z.number().int().nonnegative().optional(),
  reason: z.string(),
});
export type TechnicalTextReport = z.infer<typeof technicalTextReportSchema>;

export const proposeCardOutputSchema = z.object({
  issueIid: z.number().int().positive(),
  slots: z.array(slotVerdictSchema),
  dor: z.object({ ok: z.boolean(), missing: z.array(z.string()) }),
  dod: z.object({ ok: z.boolean(), missing: z.array(z.string()) }),
  invest: z.object({
    verdicts: z.array(investVerdictSchema),
    score: z.number().int(),
  }),
  /** True when no model was wired (local dev) — DoR/INVEST were skipped. */
  methodologySkipped: z.boolean(),
  allGreen: z.boolean(),
  acceptToken: z.string().optional(),
  /**
   * Highest event-log seq visible to the grounding scan that produced this
   * card (monotonic across the merge). The client uses it to tell a card that
   * provably predates an acked answer (suppress the re-ask) from one that saw
   * the answer and still needs input (genuine re-ask). Optional — absent on
   * the degraded no-store path and on rows predating the stamp.
   */
  groundedThroughSeq: z.number().int().nonnegative().optional(),
  /**
   * Field names with an in-flight edit the agent hasn't folded back into the
   * card yet. The client renders these amber + disarms Accept until they clear.
   * Field name only; text/seq are server-internal. Optional — old clients
   * ignore the extra key.
   */
  pendingEdits: z.array(z.object({ field: z.string() })).optional(),
  /**
   * The comprehension objection (`./comprehension.ts`) — present ONLY when the
   * judge returned a non-ok `qus.conceptually_sound` verdict for this card.
   * Absent means "no objection"; old clients ignore the extra key.
   *
   * `evidence` / `suggestion` are the judge's own sentences and must be shown
   * verbatim (the criterion LABEL bleeds — never write copy off `criterion`).
   * `suggestion` is a rephrasing proposal ABOUT the user's words: it may prompt
   * a question, and may never become a slot `value`.
   */
  comprehension: comprehensionObjectionSchema.optional(),
  /**
   * The open questions and draft rows that read like they were written for a
   * developer (`@loom/shared/prose-audit`'s strict categories), one row per
   * slot part. A blanked question renders the field's own localized copy; a
   * dropped draft row never reaches the user. MODEL-FACING: the bridge's card
   * event copies named keys only, so this never reaches a client. Absent when
   * nothing was left out.
   */
  technicalText: z.array(technicalTextReportSchema).optional(),
  /**
   * The fields this call sent, or the card held, that are not on this
   * session's card at all — today only `milestone`, in a project with no open
   * milestones (`resolveProjectReadinessSpec`). They were dropped, and `note`
   * says so in words the model acts on. MODEL-FACING, like `technicalText`:
   * the bridge's card event copies named keys only. Absent when nothing was
   * dropped.
   */
  notInSpec: z
    .object({ fields: z.array(z.string()), note: z.string() })
    .optional(),
});
export type ProposeCardOutput = z.infer<typeof proposeCardOutputSchema>;

// ---------------------------------------------------------------------------
// Persistent readiness state — the server-owned, monotonic document the
// `CardStateStore` persists per `(sessionId, issueIid)`. The gate becomes
// load → resolve this call's PATCH → monotonic-merge → recompute allGreen over
// the accumulated whole → persist; a verified field survives the agent dropping
// or re-asking it.
//
// `FieldState` carries the DURABLE PROOF (`evidenceQuote` + `greenAtSeq`) so the
// server can re-confirm an inherited green WITHOUT the agent re-citing it, and
// can tell an EVICTED turn (greenAtSeq <= log.minSeq) apart from a REVISED one
// (greenAtSeq > minSeq, quote gone). A field never touched is simply absent from
// `CardState.fields` — there is no "absent" verdict.
// ---------------------------------------------------------------------------

export const fieldStateSchema = z.object({
  field: z.string(), // a spec field name
  status: z.enum(SLOT_VERDICTS),
  /** Proposed/derived value (green/red/unavailable); absent on needs_input. */
  value: z.string().optional(),
  resolvedSource: z.enum(RESOLVED_SOURCES).nullable(),
  reason: z.string(),
  // Durable proof for non-destructive re-resolution (the invariant):
  /** The verbatim quote that proved a user green. */
  evidenceQuote: z.string().optional(),
  /** The user_message seq the quote matched when it first greened. */
  greenAtSeq: z.number().int().nonnegative().optional(),
  greenAtMs: z.number().int().nonnegative().optional(),
  updatedAtMs: z.number().int().nonnegative(),
  /** Echoed for needs_input (display-only). */
  widget: widgetSpecSchema.optional(),
  /** Loom's draft answer for a needs_input field (display-only). Persisted for
   *  the same reason `widget` is: the bridge re-emit rebuilds the card from
   *  this document, and a reattach that dropped the draft would re-open the
   *  question with an empty control. Cleared when the field greens — a drafted
   *  answer that has been answered is not a draft any more. */
  draft: draftSchema.optional(),
  /** The persisted half of `SlotVerdict.gateRefusal` — this non-green row is a
   *  refused model proposal, not a question the agent asked. Persisted rather
   *  than recomputed because the bridge-reattach re-emit rebuilds the card from
   *  this document alone, and a re-emit that dropped the flag would put the
   *  gate's own sentence back on screen on the next reconnect. */
  gateRefusal: z.boolean().optional(),
  /** The persisted half of `SlotVerdict.revised` — the model rewrote this green
   *  over the user's own value with no new user turn behind it. Survives until a
   *  merge re-proposes the same value, so a remount cannot swallow the one
   *  notice that is worth showing. Nothing the CLIENT does clears it, which is
   *  exactly why the surface it drives has to be a note and not a step. */
  revised: z.boolean().optional(),
  /** The persisted half of `SlotVerdict.meetingAnswer`: this `meeting` green is
   *  an answer from the meeting page or its link, re-confirmed against that
   *  answer (not the quotes) on every call. */
  meetingAnswer: meetingAnswerSchema.optional(),
});
export type FieldState = z.infer<typeof fieldStateSchema>;

// The methodology badges (DoR / DoD / INVEST) the live `propose_card` computes
// from a model call. Unlike the per-field verdicts, they are NOT reconstructible
// from `CardState.fields` alone, so the server persists the latest snapshot here
// to re-emit a faithful card on a bridge reattach without paying for another
// model call. See `projectCardOutput` + the bridge re-emit.
export const cardProjectionSchema = z.object({
  dor: z.object({ ok: z.boolean(), missing: z.array(z.string()) }),
  dod: z.object({ ok: z.boolean(), missing: z.array(z.string()) }),
  invest: z.object({
    verdicts: z.array(investVerdictSchema),
    score: z.number().int(),
  }),
  methodologySkipped: z.boolean(),
  /**
   * The comprehension objection — the single non-ok `qus.conceptually_sound`
   * verdict from the same methodology run (see `./comprehension.ts`). OPTIONAL
   * on purpose, twice over: rows persisted before this field existed lack it
   * (the store read is a strict `safeParse`), and ABSENT is the ordinary case —
   * it means "no objection", never "unknown" or "error".
   *
   * It is a badge and a prompt, NOT a gate: nothing here may move `allGreen`,
   * the acceptToken, or the Accept button (incident #926 — never gate on a
   * model's taste).
   */
  comprehension: comprehensionObjectionSchema.optional(),
});
export type CardProjection = z.infer<typeof cardProjectionSchema>;

// ---------------------------------------------------------------------------
// Implementation-plan tail round — the end-of-refine "simulate how this would
// be built, then validate the business change" step.
// The repo-aware sandbox agent produces this via the
// `propose_implementation_plan` tool; it is persisted onto `CardState` and
// consumed two ways:
//   - `planMarkdown` — the technical plan WITH full file paths. SERVER-SIDE
//     ONLY: it is appended to the GitLab issue description on apply and is
//     NEVER sent to the browser.
//   - `businessSummary` — a plain-language, non-technical description of what
//     changes for users/the business. The ONLY part surfaced to the client
//     (`readiness_plan_ready`), shown for the business-impact validation step.
// `status` flips `pending → confirmed` when the user answers "yes, that
// matches" on the validate screen (the `readiness_plan_confirm` WS frame, bound
// to the plan's `updatedAtMs` so the confirm names the revision the user
// actually read). Apply appends `## Implementation details` ONLY on
// `"confirmed"`: `planMarkdown` is model prose the browser never sees, so that
// one confirm is the entire user decision behind it. A later
// `propose_implementation_plan` rewrites the whole object back to `"pending"`,
// which is how a swapped plan un-confirms itself.
// ---------------------------------------------------------------------------

export const cardImplementationPlanSchema = z.object({
  planMarkdown: z.string().min(1),
  businessSummary: z.string().min(1),
  files: z.array(z.string()),
  /**
   * `"stale"` is the acceptance criteria having MOVED under a confirmed plan.
   *
   * It exists because the alternative was deleting the plan outright, and that
   * deletion was silent data loss with a live incident behind it (see the note
   * in `apply-update-issue.ts`): the user's confirm was already spent, the
   * `## Implementation details` section vanished from the issue, and re-earning
   * it cost a fresh multi-minute sandbox turn. Apply's predicate is
   * `status === "confirmed"`, so a stale plan writes exactly as much as a
   * deleted one — nothing — while staying recoverable: restoring the criteria
   * byte-for-byte (a park then un-park) restores `"confirmed"`.
   */
  status: z.enum(["pending", "confirmed", "stale"]),
  /**
   * Fingerprint of the acceptance-criteria value this plan was written against.
   * A criteria change compares against it: identical ⇒ the plan still describes
   * the work, different ⇒ `"stale"`. OPTIONAL — rows persisted before it
   * existed have none, which never matches, so they stale exactly as they
   * always did.
   */
  criteriaFingerprint: z.string().optional(),
  createdAtMs: z.number().int().nonnegative(),
  /**
   * THE PLAN'S IDENTITY, not a row mtime. It is the revision
   * `propose_implementation_plan` echoes, the value `readiness_plan_ready`
   * carries to the validate screen, and the token `readiness_plan_confirm`
   * binds to — so it moves when the PLAN changes and at no other time.
   *
   * The confirm used to stamp `updatedAtMs: now` along with the status, which
   * made a plan invalidate its own binding the moment it was validated: every
   * subsequent send of the same decision mismatched, so a reload after a
   * successful confirm re-rendered the validate screen and its "Yes, that's
   * right" bounced. `confirmedAtMs` records the decision instead.
   */
  updatedAtMs: z.number().int().nonnegative(),
  /** When the user validated THIS revision. Optional — absent on a pending or
   *  stale plan, and on any row written before the field existed. Diagnostic
   *  only: apply's predicate is `status`, never this. */
  confirmedAtMs: z.number().int().nonnegative().optional(),
});
export type CardImplementationPlan = z.infer<
  typeof cardImplementationPlanSchema
>;

// propose_implementation_plan INPUT (model-facing). Session-bound
// `sessionId`/`issueIid` are merged in server-side like propose_card; the model
// supplies only the plan payload. Plain `z.object` (no `.refine()`).
export const proposeImplementationPlanInputShape = {
  issueIid: z.number().int().positive(),
  planMarkdown: z.string().min(1),
  businessSummary: z.string().min(1),
  files: z.array(z.string()).default([]),
} as const;
export const proposeImplementationPlanInputSchema = z.object(
  proposeImplementationPlanInputShape,
);
export type ProposeImplementationPlanInput = z.infer<
  typeof proposeImplementationPlanInputSchema
>;

// ---------------------------------------------------------------------------
// In-flight ready-screen field edits. When the user edits a field on the final
// readiness preview, the server records the edit here until the agent folds it
// back into the card via propose_card (free-text fields) — or clears it in the
// same breath (widget fields, resolved server-side). A non-empty list BLOCKS
// apply (edit_pending), so a stale value can never be written under the user.
// ---------------------------------------------------------------------------
export const pendingFieldEditSchema = z.object({
  field: z.string(),
  text: z.string(), // verbatim edit text (also in the event log at `seq`)
  seq: z.number().int().nonnegative(), // seq of the user_message turn carrying the value
  requestedAtMs: z.number().int().nonnegative(),
  /** How many times a sandbox (re)attach re-sent THIS edit to the agent
   *  (`redrivePendingFieldEdits`, `apps/server/src/ws/bridge.ts`). Optional so
   *  a stored card from before the count parses; absent reads 0. A new edit of
   *  the field replaces the entry, so its count starts over. */
  redrives: z.number().int().nonnegative().optional(),
});
export type PendingFieldEdit = z.infer<typeof pendingFieldEditSchema>;

/** How often one pending edit is re-sent before the re-drive leaves it
 *  pending (amber) and sends nothing: a count, not a dollar bound. */
export const MAX_FIELD_EDIT_REDRIVES = 3;

/** The entries a re-drive may still send: those re-sent fewer than `cap`
 *  times. Pure; keeps order. */
export const redrivableEdits = (
  pending: readonly PendingFieldEdit[],
  cap: number = MAX_FIELD_EDIT_REDRIVES,
): PendingFieldEdit[] => pending.filter((p) => (p.redrives ?? 0) < cap);

export const cardStateSchema = z.object({
  sessionId: z.string(),
  issueIid: z.number().int().positive(),
  specId: z.string(),
  specVersion: z.number().int().nonnegative(),
  /** Accumulated per-field verdicts, keyed by field name. */
  fields: z.record(z.string(), fieldStateSchema),
  allGreen: z.boolean(),
  /**
   * The implementation-simulation + business-validation tail round's result.
   * OPTIONAL on purpose (same reasoning as `projection`): rows persisted before
   * this field existed lack it, and the Firestore read is a strict `safeParse`,
   * so a required field would throw on every pre-existing doc. Absent ⇒ the
   * tail round never ran (or degraded); apply appends nothing. */
  implementationPlan: cardImplementationPlanSchema.optional(),
  /**
   * The sizing tail step's result (`propose_size_estimate`): per-criterion
   * file mapping (paths persisted-only, never sent to the browser) + the
   * server-evaluated `splitRecommended` bit. OPTIONAL like
   * `implementationPlan`: pre-existing rows lack it; absent ⇒ sizing never ran
   * (or degraded) and the flow falls through to the plain accept. */
  sizeEstimate: cardSizeEstimateSchema.optional(),
  /**
   * The epic/sub-issue breakdown (`propose_breakdown` + the server-only apply
   * executor's receipts). OPTIONAL for the same back-compat reason. Lives
   * outside `fields` so neither proposing nor applying a breakdown can move
   * the acceptToken's slot hash. */
  breakdown: cardBreakdownSchema.optional(),
  /**
   * The `propose_breakdown` gate's deny budgets.
   *
   * Lives HERE, not on `breakdown`, for two reasons. A deny by definition
   * persists no proposal, so there is often no `breakdown` row to hang a
   * counter off. And `propose_breakdown` is replace-whole: anything stored
   * inside `breakdown` is wiped by the next accepted proposal, which is the
   * opposite of what a per-round thrash bound needs.
   *
   * Before this the budgets lived in a `Map` inside the tool's registration
   * closure, and `buildSessionMcpServer` runs INSIDE the `POST /mcp` route
   * handler (the stateless server-per-request pattern) — so the map was rebuilt
   * on every tool call and neither cap could ever fire. OPTIONAL: pre-existing
   * rows lack it and read as zero.
   */
  breakdownAttempts: z
    .object({
      /** Bad-partition denies. A judgement failure; degrades to a plain Accept. */
      partition: z.number().int().nonnegative(),
      /** Over-long title/scope denies. A formatting slip the model repairs
       *  verbatim, so it must not spend the partition budget on typography. */
      childText: z.number().int().nonnegative(),
    })
    .optional(),
  /**
   * Latest DoR/DoD/INVEST/methodologySkipped snapshot from the live
   * `propose_card`. OPTIONAL on purpose: rows persisted before this field
   * existed simply lack it (the Firestore read does a strict `safeParse`, so a
   * required field would throw on every pre-existing doc). The reattach re-emit
   * treats an absent projection as "can't re-emit faithfully yet" and waits for
   * the next live card rather than rendering wrong badges. */
  projection: cardProjectionSchema.optional(),
  acceptToken: z.string().optional(),
  acceptTokenExpMs: z.number().int().nonnegative().optional(),
  /** The slot-hash the acceptToken authorises — apply re-verifies ONLY this,
   *  never re-derives from agent slots (avoids the token hash-flip on a partial
   *  final PATCH). */
  acceptSlotHash: z.string().optional(),
  /** Single-use apply guard: set the first time apply consumes the token. */
  consumedAtMs: z.number().int().nonnegative().optional(),
  /**
   * The `acceptSlotHash` the consuming apply actually WROTE. It is what tells a
   * replay of that same write apart from a LATER card being applied against the
   * spent claim.
   *
   * Without it the two are indistinguishable and the consumed branch answered
   * both with the same fabricated success: the model re-carded an issue with a
   * fourth criterion, the fresh token verified, the claim short-circuited, and
   * `apply_update_issue` returned `{dor: pass, webUrl}` for a GitLab write that
   * never happened — so the agent told the user "the update is live" over an
   * unchanged body. Equal hash ⇒ genuinely the same bytes ⇒ no-op success, as
   * before. Different hash ⇒ `card_already_applied`.
   *
   * OPTIONAL: rows consumed before this field existed lack it and keep the old
   * no-op-success behaviour rather than refusing a write they cannot judge.
   */
  consumedSlotHash: z.string().optional(),
  /**
   * In-flight user edits from the ready screen that the agent has not yet
   * folded into the card via propose_card. Non-empty ⇒ apply is blocked
   * (edit_pending). `text` is kept so a bridge re-attach can re-drive the
   * directed prompt. OPTIONAL: rows written before this field parse unchanged.
   */
  pendingEdits: z.array(pendingFieldEditSchema).optional(),
  /**
   * Optional fields the user explicitly SKIPPED, by name.
   *
   * Durable so the decision outlives a remount and reaches the agent, which is
   * what stops a skipped `milestone` being re-asked for the rest of the flow.
   * Never contains a required field — the handler refuses one by name, because
   * a forged frame must not be able to retire something that gates apply.
   * OPTIONAL: rows persisted before it stay parseable.
   */
  declinedFields: z.array(z.string()).optional(),
  /**
   * Highest event-log seq the most recent grounding scan had visible —
   * monotonic max across merges (a later call can only have seen MORE of the
   * log). OPTIONAL like `projection`: pre-existing rows lack it, the store
   * read is a strict `safeParse`, and the re-emit simply omits the stamp. */
  groundedThroughSeq: z.number().int().nonnegative().optional(),
  createdAtMs: z.number().int().nonnegative(),
  updatedAtMs: z.number().int().nonnegative(),
});
export type CardState = z.infer<typeof cardStateSchema>;

/** Project a merged `CardState` into the `ProposeCardOutput` wire shape: one
 *  verdict per field in the accumulated record, ordered by the spec. The slots
 *  are derived from the MONOTONIC `state.fields` (so a latched green survives a
 *  later card re-showing red), `allGreen`/`acceptToken` come from the
 *  server-owned `state`, and the methodology badges are passed in (they are not
 *  reconstructible from `fields`). Single source of truth for both the live
 *  `propose_card` return and the bridge reattach re-emit. */
export const projectCardOutput = (
  state: CardState,
  dor: ProposeCardOutput["dor"],
  dod: ProposeCardOutput["dod"],
  invest: ProposeCardOutput["invest"],
  methodologySkipped: boolean,
  spec: ReadinessSpec,
  issueIid: number,
): ProposeCardOutput => {
  const declined = new Set(state.declinedFields ?? []);
  const slots: SlotVerdict[] = orderedFields(spec).flatMap((f) => {
    const fs = state.fields[f.name];
    if (!fs) return [];
    const v: SlotVerdict = {
      field: fs.field as SlotField,
      verdict: fs.status,
      reason: fs.reason,
      resolvedSource: fs.resolvedSource,
      ...(fs.value !== undefined ? { value: fs.value } : {}),
      ...(fs.widget ? { widget: fs.widget } : {}),
      // Only while the question is still open — same rule the live path
      // applies (`draftFor` in propose-card.ts), so a reattach re-emit and the
      // live card put the identical rows in front of the user.
      ...(fs.draft && fs.status === "needs_input" ? { draft: fs.draft } : {}),
      // Carried, not recomputed: `fs.reason` is the gate's own sentence and the
      // only thing that tells a consumer not to render it is this flag. Dropping
      // it on the re-emit would mean a reconnect re-published the refusal as a
      // question — the same defect, one socket later.
      ...(fs.gateRefusal === true && fs.status !== "green"
        ? { gateRefusal: true as const }
        : {}),
      // Only meaningful on a green (it describes what happened TO one); a
      // non-green row has no value for the user to review.
      ...(fs.revised === true && fs.status === "green"
        ? { revised: true as const }
        : {}),
      // Provenance of a meeting-page answer, on its green only.
      ...(fs.meetingAnswer && fs.status === "green" && fs.resolvedSource === "meeting"
        ? { meetingAnswer: fs.meetingAnswer }
        : {}),
      // The user's SKIP, projected onto the slot so a remount (or a reload)
      // inherits it. Rides the same path `widget` and `draft` do — the decision
      // has to be durable, or the question comes straight back.
      ...(declined.has(fs.field) ? { declined: true as const } : {}),
    };
    return [v];
  });
  const out: ProposeCardOutput = {
    issueIid,
    slots,
    dor,
    dod,
    invest,
    methodologySkipped,
    allGreen: state.allGreen,
  };
  if (state.allGreen && state.acceptToken) out.acceptToken = state.acceptToken;
  if (state.groundedThroughSeq !== undefined) {
    out.groundedThroughSeq = state.groundedThroughSeq;
  }
  // Only edits to a field this spec carries: an edit to a field the session no
  // longer asks about (a milestone, once the project turned out to have none)
  // blocks nothing — apply ignores it too — so the card must not show it.
  const onCard = new Set(spec.fields.map((f) => f.name));
  const pendingOnCard = (state.pendingEdits ?? []).filter((p) =>
    onCard.has(p.field),
  );
  if (pendingOnCard.length > 0) {
    out.pendingEdits = pendingOnCard.map((p) => ({ field: p.field }));
  }
  // The comprehension objection is read off the PERSISTED projection rather
  // than passed in like dor/dod/invest. Both are the same snapshot — the live
  // `propose_card` writes `next.projection` in the same mutate it returns — but
  // reading it from `state` means the bridge-reattach re-emit carries the
  // objection with no change at its call sites. An `ok` level is dropped
  // defensively so "present" is always "objection" for every consumer.
  const objection = state.projection?.comprehension;
  if (objection && objection.level !== "ok") out.comprehension = objection;
  return out;
};

// The declarative readiness spec — the single runtime list of fields driving
// the input allowlist, the allGreen predicate, the prompt field-list, and the
// client field order. Re-exported here so it surfaces on `@loom/shared` and
// `@loom/shared/readiness` alongside the wire contract above. (Defensively
// placed at the bottom: `spec.ts`'s `SlotField` import is type-only, so there
// is no runtime cycle, but re-exporting after `SLOT_FIELDS` is defined keeps
// the module init order unambiguous.)
export * from "./spec.js";
// Sizing (post-green estimation + split policy) and breakdown (epic/children
// partition, epic marker, `## Sub-issues` helpers) are the tail-round leaves.
export * from "./sizing.js";
export * from "./reconstruct.js";
export * from "./breakdown.js";
// The `## Design` block + `## What you need` — the two body parts the refine
// rebuild used to delete. Same carry-over rule as `## Sub-issues` / `## Build
// notes` above; `breakdown.js` imports the shared heading back-scan from here,
// so the direction is breakdown → design-section and there is no cycle.
export * from "./design-section.js";
// A draft is an OFFER, and an offer identical to the value being questioned is
// not one. Sits after `breakdown.js` because it reads the same row splitter
// every other consumer does.
export * from "./draft.js";
// The typed operation vocabulary + the pure reducer both surfaces run. Crypto-
// free on purpose: apps/web imports this barrel for runtime values, so a single
// `node:crypto` import anywhere below it would break the browser bundle.
export * from "./breakdown-ops.js";
// Store-boundary guard: keeps a mid-rollover write from an older revision from
// stripping fields a newer one added (`breakdown.parked` above all).
export * from "./forward-compat.js";
// User-slot value grounding (F1) and the tail-round timer policy (F6) — both
// pure leaves consumed by the gate and the two customer surfaces.
export * from "./grounding.js";
// The comprehension objection selector — grounding proves the bytes are the
// user's; this says whether they landed in the right slot.
export * from "./comprehension.js";
export * from "./tail-timer.js";
// The name lines a plain chat puts over each story answer it sends — read by the
// web client that sends them and by the chat directive that tells the model
// about them, so the two cannot drift.
export * from "./story-answer-labels.js";
// Grounding primitives + locale-keyed narrative templates live in the i18n leaf
// so the gate (mcp-server) imports one normalizer/parser/template set.
export {
  normalizeQuote,
  parseLabelled,
  NARRATIVE_TEMPLATES,
  SECTION_HEADINGS,
  normalizeNarrativeClause,
  // The card-level language signal. A one-word role is too small a sample for
  // the two English orthography rules, so the composer judges every clause
  // together (live #1078: "As a Kundschaft, I want Die Kundschaft findet …").
  narrativeContentIsForeign,
  // Which locale's FRAME the write sites wrap the story clauses in — the same
  // evidence under two extra floors, over the story clauses ALONE.
  resolveContentLocale,
  // The tri-state under it. `null` is NO VERDICT, never "English": nothing here
  // detects English, so the write-site logs must not report its absence as a
  // finding.
  detectStoryLanguage,
  type NarrativeTemplate,
  type NarrativeClauseOpts,
  type Locale,
  DEFAULT_LOCALE,
  asLocale,
} from "../i18n.js";
